import type { NameTranslation } from "../../api/addressBook.service";
import { isValidLocale } from "./localeValidation";

export type TranslationRowError = "invalid-locale" | "duplicate-locale" | null;

/**
 * Computes the inline error (if any) for a single translation row, and whether
 * the row is "blank" (empty locale — the placeholder state for a half-typed new
 * row). Blank rows never produce an error: they're not yet a submission concern.
 *
 * Mirrors the skip rule in translationsDelta.ts (empty locale/name rows are
 * dropped, not treated as errors).
 */
export function getTranslationRowError(
  translations: NameTranslation[],
  index: number,
): TranslationRowError {
  const trimmedLocale = translations[index].localeCode.trim();
  if (trimmedLocale === "") return null;
  if (!isValidLocale(trimmedLocale)) return "invalid-locale";
  const isDuplicate = translations.some(
    (t, i) => i !== index && t.localeCode.trim() === trimmedLocale,
  );
  return isDuplicate ? "duplicate-locale" : null;
}

/**
 * A translations list is valid for submission when no row has a non-empty
 * locale that is invalid or duplicates another row's locale. Empty/blank rows
 * (half-typed new rows) never block validity.
 */
export function areTranslationsValid(translations: NameTranslation[]): boolean {
  return translations.every((_, index) => getTranslationRowError(translations, index) === null);
}
